import { useEffect, useMemo, useState } from 'react'
import { userService } from '../../../services/api'
import { Users, UserPlus, Pencil, Trash2 } from 'lucide-react'

const emptyForm = {
  id: '',
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  address: '',
  property_invitation_code: '',
  identity_user_id: '',
  is_active: true
}

export default function TenantsList() {
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(emptyForm)

  const isEditing = useMemo(() => !!form.id, [form.id])

  const loadTenants = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await userService.getTenants({ includeInactive: true })
      const data = response?.data?.data || []
      setTenants(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err?.response?.data?.message || 'No fue posible cargar arrendatarios.')
      setTenants([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTenants()
  }, [])

  const openCreate = () => {
    setForm(emptyForm)
    setShowForm(true)
  }

  const openEdit = (tenant) => {
    setForm({
      id: tenant.id || '',
      email: tenant.email || '',
      first_name: tenant.first_name || '',
      last_name: tenant.last_name || '',
      phone: tenant.phone || '',
      address: tenant.address || '',
      property_invitation_code: tenant.property_invitation_code || '',
      identity_user_id: tenant.identity_user_id || '',
      is_active: tenant.is_active !== false
    })
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setForm(emptyForm)
  }

  const saveTenant = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      const payload = {
        email: form.email,
        firstName: form.first_name,
        lastName: form.last_name,
        phone: form.phone || null,
        address: form.address || null,
        propertyInvitationCode: form.property_invitation_code || null,
        identityUserId: form.identity_user_id || null,
        isActive: form.is_active
      }

      if (isEditing) {
        await userService.updateTenant(form.id, payload)
      } else {
        await userService.createTenant(payload)
      }

      closeForm()
      await loadTenants()
    } catch (err) {
      setError(err?.response?.data?.message || 'No fue posible guardar el arrendatario.')
    } finally {
      setSaving(false)
    }
  }

  const deleteTenant = async (tenantId) => {
    try {
      await userService.deactivateTenant(tenantId)
      await loadTenants()
    } catch (err) {
      setError(err?.response?.data?.message || 'No fue posible eliminar el arrendatario.')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Arrendatarios</h1>
          <p className="text-gray-600 mt-1">Administra tus arrendatarios activos en RentalBiz</p>
        </div>
        <button onClick={openCreate} className="btn btn-primary inline-flex items-center gap-2">
          <UserPlus className="w-4 h-4" />
          Nuevo arrendatario
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      <div className="card overflow-x-auto">
        {loading ? (
          <div className="p-6 text-gray-500">Cargando arrendatarios...</div>
        ) : tenants.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <Users className="w-10 h-10 mx-auto mb-3 text-gray-400" />
            No hay arrendatarios registrados.
          </div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Nombre</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Email</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Teléfono</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Código invitación</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Estado</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-gray-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.id} className="border-b border-gray-100">
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {`${tenant.first_name || ''} ${tenant.last_name || ''}`.trim() || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{tenant.email || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{tenant.phone || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{tenant.property_invitation_code || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${tenant.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {tenant.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button className="btn btn-secondary px-3 py-2 inline-flex items-center gap-1" onClick={() => openEdit(tenant)}>
                        <Pencil className="w-4 h-4" />
                        Editar
                      </button>
                      {tenant.can_delete && (
                        <button
                          className="btn px-3 py-2 inline-flex items-center gap-1 bg-red-50 text-red-700 hover:bg-red-100"
                          onClick={() => deleteTenant(tenant.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                          Eliminar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {isEditing ? 'Editar arrendatario' : 'Nuevo arrendatario'}
            </h2>
            <form onSubmit={saveTenant} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input className="input" placeholder="Nombre" value={form.first_name} onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))} required />
                <input className="input" placeholder="Apellido" value={form.last_name} onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))} required />
                <input className="input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} required />
                <input className="input" placeholder="Teléfono" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
                <input className="input md:col-span-2" placeholder="Dirección" value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} />
                <input className="input" placeholder="Código invitación" value={form.property_invitation_code} onChange={(e) => setForm((p) => ({ ...p, property_invitation_code: e.target.value.toUpperCase() }))} />
                <input className="input" placeholder="Identity User Id (opcional)" value={form.identity_user_id} onChange={(e) => setForm((p) => ({ ...p, identity_user_id: e.target.value }))} />
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    className="input"
                    value={form.is_active ? 'active' : 'inactive'}
                    onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.value === 'active' }))}
                  >
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn btn-secondary" onClick={closeForm}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
